import { useMemo } from "react";
import { Link } from "react-router-dom";

import FlowBars from "../../components/director/module1/FlowBars";
import MetricCard from "../../components/director/module1/MetricCard";
import ModuleShell from "../../components/director/module1/ModuleShell";
import ModuleState from "../../components/director/module1/ModuleState";
import useDirectorSurface from "../../components/director/module1/useDirectorSurface";
import { useDirectorContext } from "@contexts/director";
import { formatBps, formatCents, formatDriverCause } from "../../utils/directorFormat";
import { companyQuery, currentCompetence, moduleFilters } from "./module1Utils";

const ExecutiveOverview = () => {
  const { selectedCompanyId, filters } = useDirectorContext();
  const query = useMemo(() => moduleFilters({
    from: currentCompetence(),
    months: 24,
    scenario: "base",
    filters,
  }), [filters]);
  const summary = useDirectorSurface("executive-summary", query);
  const weekly = useDirectorSurface("cashflow/weekly", query);
  const data = summary.data;
  const inflow = data ? data.next13Weeks.actualInflowCents + data.next13Weeks.forecastInflowCents : null;
  const outflow = data ? data.next13Weeks.actualOutflowCents + data.next13Weeks.committedOutflowCents + data.next13Weeks.plannedUncommittedOutflowCents : null;
  return <ModuleShell
    title="Visão executiva consolidada"
    description="O que exige decisão agora, com cobertura e limites de fonte sempre visíveis."
    badge={data?.coverage?.valueBps ? `Cobertura ${formatBps(data.coverage.valueBps)}` : "Regime A · fluxo sem saldo"}
    qualitySources={[summary, weekly]}
  >
    <ModuleState loading={summary.loading || weekly.loading} error={summary.error || weekly.error} availability={data?.availability} onRetry={() => { summary.reload(); weekly.reload(); }} />
    {data && <>
      <section className="director-module1__metrics">
        <MetricCard label="Entradas · 13 semanas" value={formatCents(inflow)} note="Realizadas + previstas" tone="blue" />
        <MetricCard label="Saídas · 13 semanas" value={formatCents(outflow)} note="Realizadas + comprometidas" tone="orange" />
        <MetricCard label="Fluxo líquido" value={formatCents(data.next13Weeks.netFlowCents)} note="Não representa saldo bancário" tone={data.next13Weeks.netFlowCents < 0 ? "pressure" : "green"} />
      </section>
      <section className="director-module1__split">
        <article className="director-module1__panel director-module1__panel--bars">
          <header><div><span className="director-eyebrow">Fluxo econômico</span><h2>13 semanas</h2></div><p>Azul indica fluxo líquido positivo; laranja indica pressão de saídas.</p></header>
          <FlowBars items={weekly.data?.series || []} />
        </article>
        <aside className="director-module1__insight">
          <span className="director-eyebrow">Explicação determinística</span>
          <h2>{data.pressurePeriods?.length ? "Há períodos de pressão, não uma conclusão de insolvência" : "Nenhuma pressão líquida nas 13 semanas"}</h2>
          <p>{data.topDrivers?.[0] ? `O principal driver identificado é ${formatDriverCause(data.topDrivers[0].cause)}.` : "Os drivers aparecem quando as saídas superam as entradas em uma semana."}</p>
          <div className="director-module1__callout">Cobertura por valor: {formatBps(data.coverage?.valueBps)}</div>
          <Link className="director-button" to={`/dashboard/director/liquidity/causes${companyQuery(selectedCompanyId)}`}>Abrir explicação</Link>
        </aside>
      </section>
    </>}
  </ModuleShell>;
};

export default ExecutiveOverview;
